import React, { useState, useEffect, useMemo } from 'react';
import { 
    Dialog, 
    DialogContent, 
    DialogActions, 
    TextField, 
    Button, 
    Box, 
    IconButton, 
    InputAdornment,
    FormHelperText
} from '@mui/material';
import { toast } from 'react-toastify';
import { addStops, addSTMSStops, getInstitutions, getRoutes } from './action.ts'; 
import MapModal from '../Warehouses/map-modal.tsx'; 
import Dropdown from '../../Components/DropDown/drop-down.tsx'; 
import Heading from '../../Components/Heading/heading.tsx';
import mapsIcon from '../../../assets/map.png';
import AddRouteModal from '../Vehicles/add-route-modal.tsx'; 
interface AddStopsModalProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: () => void; 
    companyOptions: { id: string; label: string }[]; 
}

const AddStopsModal: React.FC<AddStopsModalProps> = ({ open, onClose, onSuccess, companyOptions }) => {
    const [name, setName] = useState('');
    const [companiesId, setCompaniesId] = useState(''); 
    const [lat, setLat] = useState('');
    const [lng, setLng] = useState('');
    const [radius, setRadius] = useState(''); 
    const [address, setAddress] = useState('');
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [institutions, setInstitutions] = useState<{ id: string; label: string }[]>([]);
    const [selectedInstitution, setSelectedInstitution] = useState('');
    const [institutionError, setInstitutionError] = useState('');
    const [routes, setRoutes] = useState<{ id: string; label: string }[]>([]);
    const [selectedRoute, setSelectedRoute] = useState('');
    const [routeError, setRouteError] = useState('');
    const [isAddRouteModalOpen, setIsAddRouteModalOpen] = useState(false);
    const [companyError, setCompanyError] = useState('');
    const [nameError, setNameError] = useState('');
    const [latError, setLatError] = useState('');
    const [lngError, setLngError] = useState('');
    const [radiusError, setRadiusError] = useState('');
    const [addressError, setAddressError] = useState('');

    const isStmsSelected = useMemo(() => {
        const selectedCompany = companyOptions?.find(c => String(c.id) === String(companiesId));
        return selectedCompany?.label?.toLowerCase() === 'stms';
    }, [companiesId, companyOptions]);

    useEffect(() => {
        if (isStmsSelected) {
            getInstitutions(
                100,
                0,
                (response: any) => {
                    const data = response?.message?.institutions_list || response?.data || [];
                    const options = data.map((item: any) => ({
                        id: String(item.id),
                        label: item.name || item.institution_name,
                    }));
                    setInstitutions(options);
                },
                (error: any) => {
                    toast.error("Failed to fetch institutions");
                    setInstitutions([]);
                }
            );
        } else {
            setInstitutions([]);
            setSelectedInstitution('');
            setInstitutionError('');
            setRoutes([]);
            setSelectedRoute('');
            setRouteError('');
        }
    }, [isStmsSelected, companiesId]);

    const fetchRoutes = (instId: string) => {
        getRoutes(
            instId,
            (response: any) => {
                const data = response?.message?.routes || response?.data || response || [];
                const options = (Array.isArray(data) ? data : []).map((item: any) => ({
                    id: String(item.id),
                    label: item.name || item.route_name || item.label || `Route ${item.id}`,
                }));
                options.push({ id: 'add_new_route', label: '+ Add New Routes' });
                setRoutes(options);
            },
            (error: any) => {
                toast.error("Failed to fetch routes");
            }
        );
    };

    const handleInstitutionChange = (e: any) => {
        const instId = String(e.target.value);
        setSelectedInstitution(instId);
        setInstitutionError('');
        setSelectedRoute('');
        setRoutes([]);
        setRouteError('');

        if (instId) {
            fetchRoutes(instId);
        }
    };

    const handleLocationSelect = (selectedLat: string, selectedLng: string) => {
        setLat(selectedLat);
        setLatError('');
        setLng(selectedLng);
        setLngError('');
    };

    const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setRadius(value);

        if (value.trim() === '') {
            setRadiusError('Radius is required');
            return;
        }

        const hasInvalidChars = /[^\d.]/.test(value); 
        const dotCount = (value.match(/\./g) || []).length; 

        if (hasInvalidChars) {
            setRadiusError('Alphabets and special characters are not allowed');
        } else if (dotCount > 1) {
            setRadiusError('Only one decimal point is allowed');
        } else {
            setRadiusError('');
        }
    };

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAddress(value);

        if (/[%*(){}\[\]]/.test(value)) {
            setAddressError('Characters % * ( ) { } [ ] are not allowed');
        } else {
            setAddressError('');
        }
    };

    const handleModalClose = () => {
        setCompanyError('');
        setInstitutionError('');
        setRouteError('');
        setNameError('');
        setLatError('');
        setLngError('');
        setRadiusError('');
        setAddressError('');
        onClose();
    };

    const handleSubmit = () => {
        let isValid = true;
        if (!companiesId) {
            setCompanyError("Company is required");
            isValid = false;
        }
        if (isStmsSelected) {
            if (!selectedInstitution) {
                setInstitutionError("Institution is required");
                isValid = false;
            }
            if (!selectedRoute) {
                setRouteError("Route is required");
                isValid = false;
            }
        }
        if (!name.trim()) {
            setNameError("Stop Name is required");
            isValid = false;
        }
        if (!lat.trim()) {
            setLatError("Latitude is required");
            isValid = false;
        }
        if (!lng.trim()) {
            setLngError("Longitude is required");
            isValid = false;
        }
        if (!radius.trim()) {
            setRadiusError("Radius is required");
            isValid = false;
        } else if (radiusError) {
            isValid = false;
        }
        
        if (addressError) {
            isValid = false;
        }

        if (!isValid) return;

        const radiusValue = Number(radius);
        if (isNaN(radiusValue)) {
            setRadiusError("Radius must be a valid number");
            return;
        }

        const handleSuccess = (response: any) => {
            if (response.status === 200 || response.status === 201) {
                toast.success("Stop added successfully");
                
                setName('');
                setCompaniesId('');
                setLat('');
                setLng('');
                setRadius('');
                setAddress('');
                setSelectedInstitution('');
                setSelectedRoute('');
                handleModalClose();
                if (onSuccess) onSuccess(); 
            } else {
                toast.error(response.message || "Failed to add stop");
            }
        };

        const handleError = (errorMessage: string) => toast.error(errorMessage);
        
        if (isStmsSelected) {
            addSTMSStops(
                name,
                address,
                companiesId, 
                lat,
                lng,
                radiusValue,
                selectedRoute, 
                handleSuccess,
                handleError
            );
        } else {
            addStops(
                name,
                address,
                companiesId, 
                lat,
                lng,
                radiusValue, 
                handleSuccess,
                handleError
            );
        }
    };

    const initialLocation = lat && lng ? { lat: parseFloat(lat), lng: parseFloat(lng) } : undefined;

    return (
        <>
            <Dialog open={open} onClose={handleModalClose} maxWidth="sm" >
                <Box sx={{ marginLeft: { xs: '0px', sm: '-180px' } }}>    
                    <Heading title="Add Stops" closeIcon="true" onClose={handleModalClose} />
                </Box>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        
                        <Box sx={{ width: '210px' }}>
                            <Dropdown 
                                options={companyOptions ? [...companyOptions].sort((a, b) => a.label.localeCompare(b.label)) : []}                
                                selectedValue={companiesId} 
                                handleChange={(e: any) => {
                                    setCompaniesId(String(e.target.value));
                                    setCompanyError('');
                                }} 
                                label="Company *"
                                placeholder="Select Company"
                                width="210px" 
                                height="40px"
                                searchable={true}
                            />
                            {companyError && <FormHelperText error sx={{ ml: 1 }}>{companyError}</FormHelperText>}
                        </Box>
                        
                        {isStmsSelected && (
                            <>
                                <Box sx={{ width: '210px' }}>
                                    <Dropdown 
                                        options={institutions}                
                                        selectedValue={selectedInstitution} 
                                        handleChange={handleInstitutionChange} 
                                        label="Institution *"
                                        placeholder="Select Institution"
                                        width="210px" 
                                        height="40px"
                                        searchable={true}
                                    />
                                    {institutionError && <FormHelperText error sx={{ ml: 1 }}>{institutionError}</FormHelperText>}
                                </Box>

                                {selectedInstitution && (
                                    <Box sx={{ width: '210px' }}>
                                        <Dropdown 
                                            options={routes}                
                                            selectedValue={selectedRoute} 
                                            handleChange={(e: any) => {
                                                const val = String(e.target.value);
                                                if (val === 'add_new_route') {
                                                    setIsAddRouteModalOpen(true);
                                                } else {
                                                    setSelectedRoute(val);
                                                    setRouteError('');
                                                }
                                            }} 
                                            label="Route *"
                                            placeholder="Select Route"
                                            width="210px" 
                                            height="40px"
                                            searchable={true}
                                        />
                                        {routeError && <FormHelperText error sx={{ ml: 1 }}>{routeError}</FormHelperText>}
                                    </Box>
                                )}
                            </>
                        )}
                        
                        <TextField 
                            label="Stop Name" 
                            required
                            value={name} 
                            onChange={(e) => {
                                setName(e.target.value);
                                setNameError('');
                            }} 
                            error={!!nameError}
                            helperText={nameError}
                            sx={{ width: '210px' }}
                            size="small" 
                        />

                        <TextField 
                            label="Address" 
                            multiline
                            rows={3}
                            value={address} 
                            onChange={handleAddressChange} 
                            error={!!addressError}
                            helperText={addressError}
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                        
                        <TextField 
                            label="Latitude" 
                            required
                            value={lat} 
                            onChange={(e) => {
                                setLat(e.target.value);
                                setLatError('');
                            }}
                            error={!!latError}
                            helperText={latError}
                            placeholder="Auto-filled from map"
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                        
                        <TextField 
                            label="Longitude"
                            required 
                            value={lng} 
                            onChange={(e) => {
                                setLng(e.target.value);
                                setLngError('');
                            }}
                            error={!!lngError}
                            helperText={lngError}
                            placeholder="Auto-filled from map"
                            sx={{ width: '210px' }}
                            size="small" 
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton 
                                            onClick={() => setIsMapOpen(true)} 
                                            color="primary" edge="end" title="Select location on map"
                                        >
                                            <img src={mapsIcon} alt="Map View" style={{ width: '30px', height: '30px' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }} 
                        />

                        <TextField 
                            label="Radius (meters)" 
                            required
                            value={radius} 
                            onChange={handleRadiusChange}
                            type="text"
                            error={!!radiusError} 
                            helperText={radiusError} 
                            sx={{ width: '210px' }}
                            size="small" 
                        />
                    </Box>
                </DialogContent>
                <DialogActions style={{ justifyContent: 'center' }}>
                    <Button onClick={handleModalClose} className="custom-cancel-button">Cancel</Button>
                    <Button onClick={handleSubmit} className="custom-button" variant="contained">Save</Button>
                </DialogActions>
            </Dialog>

            <MapModal 
                open={isMapOpen} 
                onClose={() => setIsMapOpen(false)} 
                onLocationSelect={handleLocationSelect}
                initialLocation={initialLocation}
            />

            <AddRouteModal
                open={isAddRouteModalOpen}
                institutionId={selectedInstitution}
                onClose={() => setIsAddRouteModalOpen(false)}
                onSuccess={() => {
                    if (selectedInstitution) {
                        fetchRoutes(selectedInstitution);
                    }
                }}
            />
        </>
    );
};

export default AddStopsModal;